// pages/top-selling.jsx
import React, {useEffect, useMemo, useState, useCallback, useRef} from 'react';
import {
  Page,
  Card,
  Button,
  ButtonGroup,
  DataTable,
  Popover,
  Collapsible,
  Box,
  InlineStack,
  Text,
  Divider,
  ChoiceList,
  Select,
  Spinner,
  ProgressBar,
} from '@shopify/polaris';
import BillingGate from "../components/BillingGate";
// ---------- helpers ----------
const currency = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});
const oneDecimal = (n) => (Math.round((Number(n) || 0) * 10) / 10).toFixed(1);
const pct = (n) => `${oneDecimal(n)}%`;

function getShopFromURL() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('shop') || '';
  } catch {
    return '';
  }
}

function header(label) {
  return <span style={{fontSize: 14, fontWeight: 600}}>{label}</span>;
}

function coalesce(...vals) {
  for (const v of vals) {
    if (v !== undefined && v !== null) return v;
  }
  return undefined;
}


function buildApiUrl(path, qs = {}) {
  const u = new URL(path, window.location.origin);
  Object.entries(qs).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (Array.isArray(v)) {
      if (v.length) {
        v.forEach((val) => u.searchParams.append(`${k}[]`, String(val)));
        u.searchParams.set(k, v.join(','));
      }
    } else if (`${v}` !== '') {
      u.searchParams.set(k, String(v));
    }
  });
  return u.toString();
}

// ---------- component ----------
export default function TopSellingPage() {

  // Views: only one card is shown, depending on view
  const [view, setView] = useState('best');
// Accordion open/close (default closed)
const [openVendors, setOpenVendors] = useState(false);
const [openTypes, setOpenTypes] = useState(false);
const [openCollections, setOpenCollections] = useState(false);
 // 'best' | 'worst'

  // Controls
  const shop = useMemo(() => getShopFromURL(), []);
  
  // Persisted UI preferences (per shop)
  const PREFS_KEY = useMemo(() => `ts.prefs.v1:${shop}`, [shop]);
const [range, setRange] = useState('30');          // dropdown
  const FILTERS_CACHE_KEY = useMemo(() => `ts.filters.v2:${shop}:${range}`,[shop, range]);
  const [filtersCached, setFiltersCached] = useState(false);
  const [pageSize, setPageSize] = useState('10');    // NEW: Rows dropdown (5,10,25)
  const [metric, setMetric] = useState('quantity');  // dropdown; default = quantity
  const [first] = useState(50);
  const [refreshKey, setRefreshKey] = useState(0);

  // Filters popover
  const [filtersOpen, setFiltersOpen] = useState(false);
  const toggleFilters = useCallback(() => setFiltersOpen((o) => !o), []);
  const closeFilters = useCallback(() => setFiltersOpen(false), []);

  // Filter options + selections
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [collectionOptions, setCollectionOptions] = useState([]);
  // Hydrate options cached for this (shop,range)
  useEffect(() => {
    if (!shop) return;
    try {
      const raw = sessionStorage.getItem(FILTERS_CACHE_KEY);
      if (raw) {
        const f = JSON.parse(raw);
        const vArr = Array.isArray(f?.vendors) ? f.vendors : [];
        const tArr = Array.isArray(f?.productTypes) ? f.productTypes : [];
        const cArr = Array.isArray(f?.collections) ? f.collections : [];
        setVendorOptions(vArr.map(v => ({label: String(v), value: String(v)})));
        setTypeOptions(tArr.map(t => ({label: String(t), value: String(t)})));
        const cOpts = cArr.map(c => (c && typeof c === 'object' && 'title' in c && 'id' in c)
          ? {label: c.title, value: String(c.id)}
          : {label: String(c), value: String(c)});
        setCollectionOptions(cOpts);
        setFiltersCached(true);
      } else {
        setFiltersCached(false);
      }
    } catch {
      setFiltersCached(false);
    }
  }, [shop, range, FILTERS_CACHE_KEY]);

  const [selectedVendors, setSelectedVendors] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedCollections, setSelectedCollections] = useState([]);
  // Applied filters (used for fetch; only updated on Apply/Reset)
  const [appliedVendors, setAppliedVendors] = useState([]);
  const [appliedTypes, setAppliedTypes] = useState([]);
  const [appliedCollections, setAppliedCollections] = useState([]);
  // Load UI preferences (persisted) on startup
  useEffect(() => {
    if (!shop) return;
    try {
      const raw = localStorage.getItem(PREFS_KEY);
      if (!raw) return;
      const p = JSON.parse(raw) || {};
      if (p.range != null) setRange(String(p.range));
      if (p.view) setView(p.view);
      if (p.metric) setMetric(p.metric);
      if (p.pageSize != null) setPageSize(String(p.pageSize));
      if (Array.isArray(p.selectedVendors)) setSelectedVendors(p.selectedVendors);
      if (Array.isArray(p.selectedTypes)) setSelectedTypes(p.selectedTypes);
      if (Array.isArray(p.selectedCollections)) setSelectedCollections(p.selectedCollections);
      if (Array.isArray(p.appliedVendors)) setAppliedVendors(p.appliedVendors);
      if (Array.isArray(p.appliedTypes)) setAppliedTypes(p.appliedTypes);
      if (Array.isArray(p.appliedCollections)) setAppliedCollections(p.appliedCollections);
    } catch {}
  }, [shop, PREFS_KEY]);

  // Persist UI preferences (on change)
  useEffect(() => {
    if (!shop) return;
    try {
      const prefs = {
        range,
        view,
        metric,
        pageSize,
        selectedVendors,
        selectedTypes,
        selectedCollections,
        appliedVendors,
        appliedTypes,
        appliedCollections,
      };
      localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
    } catch {}
  }, [shop, PREFS_KEY, range, view, metric, pageSize, selectedVendors, selectedTypes, selectedCollections, appliedVendors, appliedTypes, appliedCollections]);



  // Data state
  const [items, setItems] = useState([]);
    // Summary derived on the client from items (single source of truth)
    const summary = useMemo(() => {
      const arr = Array.isArray(items) ? items : [];
      if (arr.length === 0) return {count: 0, totalUnits: 0, totalRevenue: 0, top: null};

      const totalUnits = arr.reduce((s, p) => s + Number(p.quantity ?? p.qty ?? 0), 0);
      const totalRevenue = arr.reduce((s, p) => s + Number(p.revenue ?? 0), 0);

      // pick top by current metric
      const metricKey = (metric === 'revenue') ? (v => Number(v.revenue ?? 0)) : (v => Number(v.quantity ?? v.qty ?? 0));
      const top = arr.reduce((best, v) => (metricKey(v) > metricKey(best) ? v : best), arr[0]);

      return {count: arr.length, totalUnits, totalRevenue, top};
    }, [items, metric]);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  // Progress UI state (no layout change)
  const [progressVisible, setProgressVisible] = useState(false);
  const [progressPct, setProgressPct] = useState(0);
  const progressTimerRef = useRef(null);
  const progressStartRef = useRef(0);
  const [progressText, setProgressText] = useState('');

  // Sorting state for DataTable
  // Columns: [Thumb, Title, Units Sold, Inventory, Revenue, Velocity, Sell-Through%]
  // Indexes:   0       1         2            3         4         5           6
  const [sortIndex, setSortIndex] = useState(2);
  const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'

  // Reset default sort per view
  useEffect(() => {
    if (view === 'worst') {
      setSortIndex(2);
      setSortDir('asc');  // least sold first
    } else {
      setSortIndex(2);
      setSortDir('desc'); // most sold first
    }
  }, [view]);


  // Load filter options when the popover first opens
  useEffect(() => {
    if (!filtersOpen || vendorOptions.length || typeOptions.length || collectionOptions.length) return;
    if (!shop) return;

    (async () => {
      setFiltersLoading(true);
      try {
        const url = buildApiUrl('/api/product-filters', {shop});
        const res = await fetch(url, {credentials: 'include'});
        const json = await res.json();

        const vOpts = (json?.vendors || []).map((v) => ({label: v, value: v}));
        const tOpts = (json?.productTypes || []).map((t) => ({label: t, value: t}));
        const cOpts = (json?.collections || []).map((c) => ({
          label: c.title,
          value: c.id,
        }));

        setVendorOptions(vOpts);
        setTypeOptions(tOpts);
        setCollectionOptions(cOpts);
        try {
          sessionStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify({
            vendors: vOpts.map(o=>o.value),
            productTypes: tOpts.map(o=>o.value),
            collections: cOpts.map(o=>({ id: o.value, title: o.label })),
          }));
          setFiltersCached(true);
        } catch {}
      } catch {
        // leave options empty; user can retry open
      } finally {
        setFiltersLoading(false);
      }
    })();
  }, [filtersOpen, vendorOptions.length, typeOptions.length, collectionOptions.length, shop]);

  const fetchData = useCallback(async () => {
    if (!shop) return;
    setLoading(true);
    setErr(null);
    setProgressVisible(true);
    setProgressPct(1);
    setProgressText('Starting…');
    progressStartRef.current = Date.now();
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    progressTimerRef.current = setInterval(() => {
      setProgressPct(p => (p < 90 ? p + Math.max(0.5, (90 - p) * 0.08) : p));
    }, 200);
    setErr(null);
    try {
      setProgressText('Preparing request…');
      const needFilters = (() => { try { return !sessionStorage.getItem(FILTERS_CACHE_KEY); } catch { return !filtersCached; } })();
      const url = buildApiUrl('/api/products/top-selling', {
        shop,
        range,
        first,
        order: view === 'worst' ? 'asc' : 'desc',
        metric,
        vendors: appliedVendors,
        productTypes: appliedTypes,
        collections: appliedCollections,
        ...(needFilters ? { includeFilters: 1 } : {}),
      });
      setProgressText(needFilters ? 'Building filters…' : 'Fetching products…');
      const res = await fetch(url, {credentials: 'include'});
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`API ${res.status}: ${text || res.statusText}`);
      }
      setProgressText('Processing results…');
      const json = await res.json();
      // If server returned filters (when includeFilters=1), cache them for this (shop,range)
      try {
        const fv = json?.filters?.vendors ?? json?.vendors;
        const ft = json?.filters?.productTypes ?? json?.productTypes;
        const fc = json?.filters?.collections ?? json?.collections;
        if (fv || ft || fc) {
          setProgressText('Caching filters…');
          const vOpts = Array.isArray(fv) ? fv.map(v => ({label: String(v?.title ?? v), value: String(v?.handle ?? v)})) : [];
          const tOpts = Array.isArray(ft) ? ft.map(t => ({label: String(t?.title ?? t), value: String(t?.handle ?? t)})) : [];
          const cOpts = Array.isArray(fc) ? fc.map(c => ({label: String(c?.title ?? c), value: String(c?.id ?? c)})) : [];
          if (vOpts.length && !vendorOptions.length) setVendorOptions(vOpts);
          if (tOpts.length && !typeOptions.length) setTypeOptions(tOpts);
          if (cOpts.length && !collectionOptions.length) setCollectionOptions(cOpts);
          try {
            sessionStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify({
              vendors: vOpts.map(o=>o.value),
              productTypes: tOpts.map(o=>o.value),
              collections: cOpts.map(o=>({ id: o.value, title: o.label })),
            }));
            setFiltersCached(true);
          } catch {}
        }
      } catch {}
      const incoming = Array.isArray(json?.items) ? json.items : [];
      setProgressText('Rendering…');
      setItems(incoming);
    } catch (e) {
      setErr(e?.message || 'Failed to load');
      setItems([]);
    } finally {
      if (progressTimerRef.current) { clearInterval(progressTimerRef.current); progressTimerRef.current = null; }
      const MIN_SHOW = 500; const elapsed = Date.now() - (progressStartRef.current||0);
      setProgressPct(100);
      setProgressText('Done');
      const hide = () => setProgressVisible(false);
      if (elapsed < MIN_SHOW) setTimeout(hide, MIN_SHOW - elapsed); else hide();
      setLoading(false);
    }
  }, [shop, range, first, view, metric, appliedVendors, appliedTypes, appliedCollections]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const rows = useMemo(() => {
    const days = Math.max(1, Number(range) || 30);

    const mapped = items.map((p) => {
      const units = Number(p.quantity ?? 0);
 const inv = Number(coalesce(
   p.totalInventory,       // preferred
   p.inventory,            // some APIs back it as "inventory"
   p.inventoryQuantity,    // Shopify variant field
   p.total_inventory,      // snake-case fallback
   p.onHand, p.on_hand,    // other dataset names
   0
));
      const rev = Number(p.revenue ?? 0);
      const velocity = units / days;
      const sellThrough = (units + inv) > 0 ? (units / (units + inv)) * 100 : 0;

      return {
        key: p.id,
        thumb: (
          <img
            src={p.image}
            alt={p.imageAlt || p.title || 'Product image'}
            width={40}
            height={40}
            style={{borderRadius: 6, objectFit: 'cover'}}
          />
        ),
 title: (
   (p.variantTitle &&
    p.variantTitle !== 'Default Title' &&
    p.variantTitle !== (p.productTitle || p.title))
     ? `${p.productTitle || p.title || 'Untitled'} - ${p.variantTitle}`
     : (p.productTitle || p.title || 'Untitled')
 ),
        units,
        inventory: inv,
        revenue: rev,
        velocity,
        sellThrough,
      };
    });

    // Client-side sorting
    const dirMul = sortDir === 'asc' ? 1 : -1;
    const sorters = [
      () => 0,                                  // thumb (unsortable)
      (a, b) => a.title.localeCompare(b.title), // Title
      (a, b) => a.units - b.units,              // Units Sold
      (a, b) => a.inventory - b.inventory,      // Inventory
      (a, b) => a.revenue - b.revenue,          // Revenue
      (a, b) => a.velocity - b.velocity,        // Velocity
      (a, b) => a.sellThrough - b.sellThrough,  // Sell-Through%
    ];
    if (sortIndex > 0 && sortIndex < sorters.length) {
      mapped.sort((a, b) => dirMul * sorters[sortIndex](a, b));
    }

    return mapped.map((r) => ([
      r.thumb,
      r.title,
      r.units.toLocaleString(),
      r.inventory.toLocaleString(),
      currency.format(r.revenue || 0),
      oneDecimal(r.velocity),
      pct(r.sellThrough),
    ]));
  }, [items, range, sortIndex, sortDir]);

  const headings = [
    header(''),
    header('Title'),
    header('Units Sold'),
    header('Inventory'),
    header('Revenue'),
    header('Velocity'),
    header('Sell-Through%'),
  ];
  const columnContentTypes = ['text','text','numeric','numeric','numeric','numeric','numeric'];

  const onSort = useCallback((columnIndex, direction) => {
    setSortIndex(columnIndex);
    setSortDir(direction === 'ascending' ? 'asc' : 'desc');
  }, []);

  const cardTitle = view === 'worst' ? 'Worst Sellers' : 'Best Sellers';

  // Dropdown options
  const rangeOptions = [
    {label: '7 days', value: '7'},
    {label: '30 days', value: '30'},
    {label: '90 days', value: '90'},
    {label: '365 days', value: '365'},
  ];
  const metricOptions = [
    {label: 'Units Sold', value: 'quantity'},
    {label: 'Revenue', value: 'revenue'},
  ];
  const rowsOptions = [
    {label: '5', value: '5'},
    {label: '10', value: '10'},
    {label: '25', value: '25'},
  ];

  return (
     <BillingGate>
    <Page title="Variant Velocity Dashboard">
      {/* Controls row */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 16,
        marginBottom: 12,
        flexWrap: 'wrap',
        justifyContent: 'space-between',
      }}>
        {/* Left: Views */}
        <div>
          <div style={{fontSize: 16, fontWeight: 600, marginBottom: 6}}>Views</div>
          <ButtonGroup>
	  <ButtonGroup>
  <Button
    tone="success"
    variant={view === "best" ? "primary" : "secondary"}
    onClick={() => setView("best")}
  >
    Best
  </Button>
  <Button
    tone="critical"
    variant={view === "worst" ? "primary" : "secondary"}
    onClick={() => setView("worst")}
  >
    Worst
  </Button>
</ButtonGroup>
          </ButtonGroup>
        
</div>

        {/* Middle: Range + Rows + Metric */}
        <div style={{display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap'}}>
          <div style={{minWidth: 180}}>
            <div style={{fontSize: 16, fontWeight: 600, marginBottom: 6}}>Range</div>
            <Select
              options={rangeOptions}
              onChange={setRange}
              value={range}
            />
          </div>

          {/* NEW: Rows dropdown (after Range) */}
          <div style={{minWidth: 120}}>
            <div style={{fontSize: 16, fontWeight: 600, marginBottom: 6}}>Rows</div>
            <Select
              options={rowsOptions}
              onChange={setPageSize}
              value={pageSize}
            />
          </div>

          <div style={{minWidth: 200}}>
            <div style={{fontSize: 16, fontWeight: 600, marginBottom: 6}}>Metric</div>
            <Select
              options={metricOptions}
              onChange={setMetric}
              value={metric}
            />
          </div>
        </div>

        {/* Right: Filters popover + Refresh */}
        <div>
          <div style={{fontSize: 16, fontWeight: 600, marginBottom: 6, textAlign: 'right'}}>Actions</div>
          <ButtonGroup>
            <Popover
              active={filtersOpen}
              activator={
                <Button tone="success" variant="primary" onClick={toggleFilters}>Filters</Button>
              }
              onClose={closeFilters}
              sectioned
            >
              <div style={{width: 320, paddingRight: 4}}>
                {/* Vendors (accordion) */}
  <Button
    fullWidth
    tone="inherit"
    variant="tertiary"
    disclosure={openVendors ? 'up' : 'down'}
    onClick={() => setOpenVendors(v => !v)}
  >
    Vendors{selectedVendors.length ? ` (${selectedVendors.length})` : ''}
  </Button>
  <Collapsible open={openVendors}>
    <div style={{padding: '12px 0'}}>
      <ChoiceList
        title=""
        allowMultiple
        selected={selectedVendors}
        onChange={setSelectedVendors}
        choices={vendorOptions}
      />
    </div>
  </Collapsible>

  <div style={{height: 12}} />

  {/* Product types (accordion) */}
  <Button
    fullWidth
    tone="inherit"
    variant="tertiary"
    disclosure={openTypes ? 'up' : 'down'}
    onClick={() => setOpenTypes(v => !v)}
  >
    Product types{selectedTypes.length ? ` (${selectedTypes.length})` : ''}
  </Button>
  <Collapsible open={openTypes}>
    <div style={{padding: '12px 0'}}>
      <ChoiceList
        title=""
        allowMultiple
        selected={selectedTypes}
        onChange={setSelectedTypes}
        choices={typeOptions}
      />
    </div>
  </Collapsible>

  <div style={{height: 12}} />

  {/* Collections (accordion) */}
  <Button
    fullWidth
    tone="inherit"
    variant="tertiary"
    disclosure={openCollections ? 'up' : 'down'}
    onClick={() => setOpenCollections(v => !v)}
  >
    Collections{selectedCollections.length ? ` (${selectedCollections.length})` : ''}
  </Button>
  <Collapsible open={openCollections}>
    <div style={{padding: '12px 0'}}>
      <ChoiceList
        title=""
        allowMultiple
        selected={selectedCollections}
        onChange={setSelectedCollections}
        choices={collectionOptions}
      />
    </div>
  </Collapsible>

  <div style={{height: 12}} /><div style={{display: 'flex', justifyContent: 'flex-end', marginTop: 12, gap: 8}}>
                  <Button
                    onClick={() => {
                      setSelectedVendors([]);
                      setSelectedTypes([]);
                      setSelectedCollections([]);
                      setAppliedVendors([]);
                      setAppliedTypes([]);
                      setAppliedCollections([]);
                      setRefreshKey((k) => k + 1);
                    }}
                    tone="critical"
                    variant="tertiary"
                  >
                    Reset
                  </Button>
                  <Button
                    tone="success"
                    variant="primary"
                    onClick={() => {
                      setAppliedVendors(selectedVendors);
                      setAppliedTypes(selectedTypes);
                      setAppliedCollections(selectedCollections);
                      closeFilters();
                      setRefreshKey((k) => k + 1);
                    }}
                    disabled={filtersLoading}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </Popover>

            <Button tone="success" variant="primary" onClick={() => setRefreshKey((k) => k + 1)}>
              Refresh
            </Button>
          </ButtonGroup>
        </div>
      </div>

      {/* Single full-width card based on selected view */}
      <Card>
        <div style={{padding: 16, paddingBottom: 8}}>
          <div style={{fontSize: 18, fontWeight: 600, marginBottom: 4}}>
            {view === 'worst' ? 'Worst Sellers' : 'Best Sellers'}
          </div>
          <div style={{color: 'var(--p-color-text-subdued, #6d7175)'}}>
            {view === 'worst'
              ? 'Variants with the lowest units sold in selected range'
              : 'Variants with the highest units sold in selected range'}
          </div>
        </div>

        {(loading || progressVisible) ? (
          <div style={{padding: 16}}>
            <div style={{maxWidth: 520, width: '100%', margin: '0 auto'}}>
              <ProgressBar progress={Math.round(progressPct)} />
              <div style={{ marginTop: 8, fontFamily: 'monospace', textAlign: 'center' }}>
                {progressText || 'Loading…'} {Math.round(progressPct)}%
              </div>
            </div>
          </div>
        ) : err ? (
          <div style={{padding: 16}}>
            <div style={{color: 'var(--p-color-text-critical, #d72c0d)', fontWeight: 600}}>
              Couldn’t load data
            </div>
            <div style={{marginTop: 8, color: 'var(--p-color-text-subdued, #6d7175)'}}>{err}</div>
          </div>
        ) : (
          <DataTable
            columnContentTypes={['text','text','numeric','numeric','numeric','numeric','numeric']}
            headings={[
              header(''),
              header('Title'),
              header('Units Sold'),
              header('Inventory'),
              header('Revenue'),
              header('Velocity'),
              header('Sell-Through%'),
            ]}
            rows={rows.slice(0, Number(pageSize) || 10)}  // limit visible rows
            sortable={[false, true, true, true, true, true, true]}
            initialSortColumnIndex={sortIndex}
            defaultSortDirection={sortDir === 'asc' ? 'ascending' : 'descending'}
            onSort={(columnIndex, direction) => {
              setSortIndex(columnIndex);
              setSortDir(direction === 'ascending' ? 'asc' : 'desc');
            }}
            stickyHeader
          />
        )}
      </Card>
<style>{`
  /* Make filter accordion headers left-aligned, bold, and larger (inside Popover) */
  .Polaris-Popover .Polaris-Button .Polaris-Button__Content {
    width: 100% !important;
    display: flex !important;
    justify-content: flex-start !important; /* left-align */
    align-items: center !important;
    text-align: left !important;
  }

  /* Push the disclosure chevron to the far right */
  .Polaris-Popover .Polaris-Button .Polaris-Button__Icon {
    margin-left: auto !important;
  }

  /* The label text Polaris renders as <span class="Polaris-Text--root Polaris-Text--bodySm Polaris-Text--medium">Vendors</span> */
  .Polaris-Popover .Polaris-Button .Polaris-Text--root.Polaris-Text--bodySm.Polaris-Text--medium {
    font-weight: 700 !important;  /* bold */
    font-size: 14px !important;   /* larger */
    line-height: 1.4 !important;
    text-align: left !important;
  }
`}</style>
    </Page>
  </BillingGate>
  );
}

